import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ModuleFlow, handleError } from '../../../../shared/utils/error.utils';
import { buildHttpParams } from '../../../../shared/utils/http.utils';
import { ApiResponse } from '../../../../shared/dto/response/api-response.dto';
import { getApiBaseUrl } from '../../../../shared/config/runtime-config';
import { UploadedFileRepository } from '../../domain/repository';
import {
  UploadedFileErrorsRequest,
  UploadedFileErrorsResponse,
  UploadedFileFindAllRequest,
  UploadedFileFindAllResponse,
  UploadedFileFindRequest,
  UploadedFileFindResponse,
  UploadedFileSaveRequest,
  UploadedFileSaveResponse,
  UploadedFileTemplateDownloadResponse,
  UploadedFileTemplateDownloadRequest,
} from '../../domain/dto';
import { UploadErrors, UploadFileResult, UploadedFile } from '../../domain/model';

interface UploadedFileApiListResponse {
  items: Array<Record<string, unknown>>;
  page: number;
  pageSize: number;
  totalCount: number;
}

@Injectable({ providedIn: 'root' })
export class UploadedFileRepositoryImpl implements UploadedFileRepository {
  constructor(private http: HttpClient) {}

  findAllPath(): string {
    return this.apiUrl;
  }

  findAll(payload: UploadedFileFindAllRequest): Observable<HttpResponse<UploadedFileFindAllResponse>> {
    const params = buildHttpParams(payload);

    return this.http
      .get<ApiResponse<UploadedFileApiListResponse>>(this.findAllPath(), {
        observe: 'response',
        params,
      })
      .pipe(
        map((response) => {
          const data = response.body?.data;
          const mappedItems = (data?.items ?? []).map((item) => this.mapApiUploadedFile(item));
          const mappedBody: UploadedFileFindAllResponse = {
            items: mappedItems,
            page: data?.page ?? 1,
            pageSize: data?.pageSize ?? 50,
            totalCount: data?.totalCount ?? 0,
          };
          return response.clone({ body: mappedBody });
        }),
        catchError((error) => handleError(ModuleFlow.UploadedFileFindAll, error)),
      );
  }

  find(payload: UploadedFileFindRequest): Observable<HttpResponse<UploadedFileFindResponse>> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(`${this.apiUrl}/${payload.uploadId}`, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: this.mapApiUploadedFile(response.body?.data) })),
        catchError((error) => handleError(ModuleFlow.UploadedFileFind, error)),
      );
  }

  save(payload: UploadedFileSaveRequest): Observable<HttpResponse<UploadedFileSaveResponse>> {
    const formData = new FormData();
    formData.append('file', payload.file, payload.file.name);
    formData.append('entity', payload.entity ?? '');

    if (payload.name) {
      formData.append('name', payload.name);
    }

    if (payload.description) {
      formData.append('description', payload.description);
    }

    return this.http
      .post<ApiResponse<Record<string, unknown>>>(`${this.apiUrl}/upload`, formData, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: this.mapApiUploadFileResult(response.body?.data) })),
        catchError((error) => handleError(ModuleFlow.UploadedFileSave, error)),
      );
  }

  findErrors(payload: UploadedFileErrorsRequest): Observable<HttpResponse<UploadedFileErrorsResponse>> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(`${this.apiUrl}/${payload.uploadId}/errors`, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: this.mapApiUploadErrors(response.body?.data) })),
        catchError((error) => handleError(ModuleFlow.UploadedFileFind, error)),
      );
  }

  downloadTemplate(
    request: UploadedFileTemplateDownloadRequest,
  ): Observable<HttpResponse<UploadedFileTemplateDownloadResponse>> {
    return this.http
      .get(`${this.apiUrl}/template-download`, {
        observe: 'response',
        params: { isAdmin: String(request.isAdmin) },
        responseType: 'blob',
      })
      .pipe(
        map((response) => response as HttpResponse<UploadedFileTemplateDownloadResponse>),
        catchError((error) => handleError(ModuleFlow.UploadedFileFind, error)),
      );
  }

  private mapApiUploadedFile(apiItem: unknown): UploadedFile {
    const item = this.toRecord(apiItem);
    return {
      id: this.toNullableNumber(item['id']),
      name: this.toNullableString(item['name']),
      description: this.toNullableString(item['description']),
      entity: this.toNullableString(item['entity']),
      status: this.toNullableString(item['status']),
      fileName: this.toNullableString(item['fileName']),
      fileType: this.toNullableString(item['fileType']),
      fileSize: this.toNullableNumber(item['fileSize']),
      created: this.toNullableString(item['created']),
      staffName: this.toNullableString(item['staffName']),
      guestCount: this.toNullableNumber(item['guestCount']),
    };
  }

  private mapApiUploadFileResult(apiItem: unknown): UploadFileResult {
    const item = this.toRecord(apiItem);
    const errors = Array.isArray(item['errors']) ? item['errors'] : [];

    return {
      uploadId: this.toNullableNumber(item['uploadId']),
      fileName: this.toNullableString(item['fileName']),
      status: this.toNullableString(item['status']),
      totalRows: this.toNullableNumber(item['totalRows']),
      validRows: this.toNullableNumber(item['validRows']),
      errorRows: this.toNullableNumber(item['errorRows']),
      errors: errors.map((error) => {
        const record = this.toRecord(error);
        return {
          row: this.toNullableNumber(record['row'] ?? record['rowNumber']),
          identification: this.toNullableString(record['identification']),
          field: this.toNullableString(record['field']),
          error: this.toNullableString(record['error']),
        };
      }),
    };
  }

  private mapApiUploadErrors(apiItem: unknown): UploadErrors {
    const item = this.toRecord(apiItem);
    const errors = Array.isArray(item['errors']) ? item['errors'] : [];

    return {
      uploadId: this.toNullableNumber(item['uploadId']),
      fileName: this.toNullableString(item['fileName']),
      errors: errors.map((error) => {
        const record = this.toRecord(error);
        return {
          row: this.toNullableNumber(record['row'] ?? record['rowNumber']),
          identification: this.toNullableString(record['identification']),
          field: this.toNullableString(record['field']),
          error: this.toNullableString(record['error']),
        };
      }),
    };
  }

  private toNullableNumber(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toNullableString(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const parsed = String(value).trim();
    return parsed.length > 0 ? parsed : null;
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private get apiUrl(): string {
    return getApiBaseUrl('uploaded-files');
  }
}


